/** leads/portalFmt.ts — 리드포털 v4 공용 헬퍼 (LeadPortal · LeadPortalInfoSheet 공유, 200줄 원칙 분리)
 *  유형 정의 · 상태→카테고리 · 날짜/D-day · 인증 유효기간 계산. 순수 함수만 (React 없음). (2026-09-05) */
import type { PortalLead, LeadStatus } from './types';
import { fmtAmount } from './calc';

/* ── 디자인 토큰 (한화 법인DB 앱 계승 — 코랄 오렌지 + 연보라 바탕) ── */
export const C = { coral: '#E25D3B', coralD: '#C94D2D', coralT: '#FDEEE9', bg: '#F2F1F6', card: '#FFFFFF', line: '#ECEAF3', ink: '#23222B', body: '#3A3F47', sub: '#8C8B98' };

/* ── DB 유형 ── */
export interface TypeDef { label: string; fg: string; tint: string; cert: boolean; desc: string }
export const TYPE_DEF: Record<string, TypeDef> = {
  mainbiz_expiry: { label: '메인비즈', fg: '#2E7D5B', tint: '#E7F2EC', cert: true, desc: '메인비즈 인증 만료 예정' },
  innobiz_expiry: { label: '이노비즈', fg: '#2563C9', tint: '#E7EEFB', cert: true, desc: '이노비즈 인증 만료 예정' },
  venture_expiry: { label: '벤처', fg: '#E36A1E', tint: '#FCEEE0', cert: true, desc: '벤처기업 확인 만료 예정' },
  venture_refund: { label: '경정청구', fg: '#0E7490', tint: '#E0F2F7', cert: false, desc: '벤처투자 소득공제 경정청구 대상' },
  venture_new: { label: '신규법인', fg: '#6B4FBB', tint: '#EFEAFB', cert: false, desc: '벤처확인 · 설립 출자 소득공제' }, // 2026-10-17
};
export const tdef = (t?: string): TypeDef => TYPE_DEF[t || ''] || { label: t || 'DB', fg: '#6B4FBB', tint: '#EFEAFB', cert: false, desc: '' };

/* ── 상태 → 카테고리 (앱 용어) ── */
export const CAT_LABEL: Record<string, string> = { assigned: '미팅예정', contacting: '미팅진행중', consulting: '미팅진행중', in_progress: '미팅진행중', hold: '미팅완료', won: '계약완료' };
export const CAT_STYLE: Record<string, { fg: string; bg: string }> = {
  미팅예정: { fg: '#FFFFFF', bg: '#5B7BD5' }, 미팅진행중: { fg: '#FFFFFF', bg: C.coral }, 미팅완료: { fg: '#FFFFFF', bg: '#8A879A' }, 계약완료: { fg: '#FFFFFF', bg: '#2E9E6B' },
};
export const cat = (s: LeadStatus): string => CAT_LABEL[s] || '미팅예정';
export const CATS = ['전체', '미팅예정', '미팅진행중', '미팅완료', '계약완료'];
export const MEET_TYPES = ['인증 재신청', '정책자금', '대출 우대', '정관변경 및 정비', '기타'];

/* ── 날짜 ── */
const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
export function day10(v?: string | null): string { return v ? String(v).slice(0, 10) : ''; }
/** 2026-09-02 → "2026년 9월 02일(수)" (앱 표기 그대로 — 일은 2자리) */
export function fmtKo(d?: string | null): string {
  const m = day10(d).match(YMD);
  if (!m) return '-';
  const dt = new Date(+m[1], +m[2] - 1, +m[3]);
  const yo = ['일', '월', '화', '수', '목', '금', '토'][dt.getDay()];
  return `${m[1]}년 ${+m[2]}월 ${m[3]}일(${yo})`;
}
/** 2026-09-10 → "2026. 9. 10.(목)" — 수락하기 카드 메타 「배정 2026. 9. 10.(목)」 (「지도사 배정」 표기는 사용자 지시로 제거, 2026-09-18) */
export function fmtDotYo(d?: string | null): string {
  const m = day10(d).match(YMD);
  if (!m) return '-';
  const dt = new Date(+m[1], +m[2] - 1, +m[3]);
  const yo = ['일', '월', '화', '수', '목', '금', '토'][dt.getDay()];
  return `${m[1]}. ${+m[2]}. ${+m[3]}.(${yo})`;
}
/** 2026-09-02 → "2026.9.2" */
export function fmtDot(d?: string | null): string {
  const m = day10(d).match(YMD);
  return m ? `${m[1]}.${+m[2]}.${+m[3]}` : '-';
}
export function dday(expiry?: string | null): number | null {
  const m = day10(expiry).match(YMD);
  if (!m) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((new Date(+m[1], +m[2] - 1, +m[3]).getTime() - t.getTime()) / 86400000);
}
export function ddayText(d: number | null): string { return d == null ? '' : d < 0 ? '만료됨' : d === 0 ? 'D-Day' : 'D-' + d; }
/** D-day 칩 색 — 7일 이내 빨강, 30일 이내 주황, 그 외 회색 */
export function ddayStyle(d: number | null): { fg: string; bg: string } {
  if (d == null || d < 0) return { fg: '#6E6C7C', bg: '#ECEAF3' };
  if (d <= 7) return { fg: '#C5382C', bg: '#FBE9E7' };
  if (d <= 30) return { fg: '#C07A22', bg: '#FCF1E2' };
  return { fg: '#6E6C7C', bg: '#ECEAF3' };
}

/* ── 인증 유효기간 ── */
const CERT_ROW: Record<string, { years?: number }> = { venture_refund: {}, venture_expiry: {}, innobiz_expiry: { years: 3 }, mainbiz_expiry: { years: 3 } };
/** 만료일에서 인증 시작일 역산 — 만료일 - N년 + 1일 (원본 명단에 시작일 컬럼이 없는 이노비즈·메인비즈) */
function backStart(iso: string, years: number): string {
  const m = day10(iso).match(YMD);
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]) - years, Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
export interface CertPeriod { text: string; to?: string; derived?: number }
/** 유형별 유효기간. 벤처 2종은 시작일이 있어 기간으로, 이노·메인비즈는 만료일 기준 3년 역산(표시에 '3년 기준' 뱃지). */
export function certPeriod(l: PortalLead): CertPeriod | null {
  const cfg = CERT_ROW[l.lead_type || ''];
  const d = l.detail;
  if (!cfg || !d) return null;
  if (l.lead_type === 'venture_refund' || l.lead_type === 'venture_expiry') {
    const f = day10(d.venture_from);
    const t = day10(l.lead_type === 'venture_expiry' ? d.expire_date : d.venture_to);
    if (!f && !t) return null;
    return { text: f && t ? f + ' ~ ' + t : (t ? '~ ' + t : f + ' ~'), to: t || undefined };
  }
  const t = day10(d.expire_date);
  if (!t) return null;
  const f = cfg.years ? backStart(t, cfg.years) : '';
  return f ? { text: f + ' ~ ' + t, to: t, derived: cfg.years } : { text: t + ' 만료', to: t };
}
export const expiryOf = (l: PortalLead): string | undefined => (l.detail?.expire_date ? day10(l.detail.expire_date) : undefined);
/** 신규법인 — 벤처확인 기한(설립연도+2년 12월 31일). 설립 출자는 그 해 ~ 2년 뒤 해 중 한 해에 공제받고, 그 해에 벤처기업이어야 한다 (2026-10-17) */
export const newDeadlineOf = (l: PortalLead): string | undefined => {
  const y = Number(l.detail?.estb_year);
  return l.lead_type === 'venture_new' && Number.isInteger(y) && y > 1990 ? (y + 2) + '-12-31' : undefined;
};

/** 카드 한 줄 요약 — "경정청구 · 2023년 5,000만원 투자" / "메인비즈 · 인증만료 2026.10.31" */
export function typeLine(l: PortalLead): string {
  const t = tdef(l.lead_type);
  if (t.cert) { const e = expiryOf(l); return t.label + ' · 인증만료' + (e ? ' ' + fmtDot(e) : ' 예정'); }
  if (l.lead_type === 'venture_refund' && l.detail?.invest_year) return `${t.label} · ${l.detail.invest_year}년 ${fmtAmount(l.detail.invest_amount)} 투자`;
  if (l.lead_type === 'venture_new') { const e = newDeadlineOf(l); return t.label + (l.detail?.estb_year ? ' · ' + l.detail.estb_year + '년 설립' : '') + (e ? ' · 벤처확인 기한 ' + fmtDot(e) : ''); }
  return t.label + (t.desc ? ' · ' + t.desc : '');
}

/** 카드 안내 문구 — 상태·미팅 등록 여부로 분기 (앱 원문 계승) */
export function guideText(l: PortalLead): string {
  if (l.status === 'won') return '계약이 완료된 법인 DB입니다.';
  if (l.status === 'hold') return '관리중인 법인 DB입니다.';
  if (l.status === 'assigned') return '새로 수락한 법인 DB입니다.\n법인정보를 확인하고 미팅을 준비해주세요.';
  if ((l.meeting_count || 0) === 0) return '법인 고객과 미팅은 어떠셨나요?\n미팅 결과를 등록해주세요.';
  return '관리중인 법인 DB입니다.';
}

/** 금액(원) → 만원 단위 문자열. null → '—' */
export function manwon(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return Math.round(v / 10000).toLocaleString();
}
